import React, { useEffect, useState } from "react";
import axios from "axios";
import { useFormik } from "formik";
import * as Yup from "yup";
import "../../styles/plans.css";
import Modal from "../Modal/Modal";
import "../../styles/thankyou.css";
import { API_BASE_URL, CRM_URL, SITE_KEY_V2 } from "../../config/config";

import ReCAPTCHA from "react-google-recaptcha";

import {
  getApiAccessToken,
  sanitizeInput,
  xssSafeString,
} from "../../utils/helper";

export default function PlansTable({ title, plans, type }) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errorMsg, setErrorMsg] = useState(false);
  const [getAuthToken, setAuthToken] = useState(null);
  const [captchaToken, setCaptchaToken] = useState(null);


  // ---------------------- GET TOKEN ----------------------
  useEffect(() => {
    const fetchToken = async () => {
      try {
        let result = await getApiAccessToken();
        setAuthToken(result.token);
      } catch (error) {
        console.error("Error fetching token:", error);
      }
    };
    fetchToken();
    const timer = setInterval(fetchToken, 60000);
    return () => clearInterval(timer);
  }, []);

  // ---------------------- FORMIK ----------------------
  const formik = useFormik({
    initialValues: {
      name: "",
      company: "",
      job: "",
      email: "",
      phone: "",
      city: "",
      description: "",
    },

    validationSchema: Yup.object({
      name: xssSafeString("Please Enter Name")
        .max(50, "Must be 50 characters or less")
        .required("Please Enter Name"),

      company: xssSafeString("Please Enter Company Name").max(
        50,
        "Must be 50 characters or less"
      ),

      job: xssSafeString("Please Enter Job Title").max(
        50,
        "Must be 50 characters or less"
      ),

      email: Yup.string()
        .required("Please Enter Email Address")
        .matches(
          /^[ ]*([^@\s]+)@((?:[-a-z0-9]+\.)+[a-z]{2,})[ ]*$/i,
          "Please enter a valid email"
        )
        .max(50, "Must be 50 characters or less")
        .transform((val) => sanitizeInput(val)),

      phone: Yup.string()
        .required("Please Enter Phone Number")
        .matches(/^\d{10}$/, "Phone number must be 10 digits")
        .transform((val) => sanitizeInput(val)),

      city: xssSafeString("Please Enter City").max(
        50,
        "Must be 50 characters or less"
      ),

      description: xssSafeString("Please Enter Description").required(
        "Please Enter Description"
      ),
    }),

    onSubmit: async (values, { resetForm }) => {
      try {
        const urlParams = new URLSearchParams(window.location.search);

        const newNav = {
          name: sanitizeInput(values.name),
          email_address: sanitizeInput(values.email),
          comp_name: sanitizeInput(values.company),
          job_title: sanitizeInput(values.job),
          phone_no: sanitizeInput(values.phone),
          description: sanitizeInput(values.description),
          city: sanitizeInput(values.city),
          service_type: "cloud-plans",
          utm_source: sanitizeInput(urlParams.get("utm_source") || ""),
          utm_medium: sanitizeInput(urlParams.get("utm_medium") || ""),
        };
        if (!captchaToken) {
          alert("Please verify that you are not a robot");
          return;
        }



        let payload = {
          Details: newNav,
          token: captchaToken,
        };


        const response = await axios.post(`${CRM_URL}?cmd=enterprise_inquiry`, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${getAuthToken}`,
          },
        });

        if (response.data.Status === "Success" || response.data.StatusCode === 200) {
          await axios.post(
            `${API_BASE_URL}/api/website/createColocationInquiry`,
            payload
          );

          setSubmitted(true);
          setCaptchaToken(null);
          resetForm();
        } else {
          setErrorMsg(true);
        }
      } catch (error) {
        setErrorMsg(true);
        console.error("Submission Error:", error);
      }
    },
  });

  // ---------------------- OPEN MODAL WITH AUTO DESCRIPTION ----------------------
  const openModal = (plan) => {
    const autoDescription =
      `I want to enquire about the ${plan.title} plan.\n\nSpecs:\n` +
      `- CPU: ${plan.cpu || "-"}\n` +
      `- RAM: ${plan.ram || "-"} GB\n` +
      `- Storage: ${plan.storage} GB\n` +
      `- Bandwidth: ${plan.bandwidth}\n` +
      `- Price: ₹${plan.price}\n\n` +
      `Please provide more details.`;

    formik.setFieldValue("description", autoDescription);
    setSubmitted(false);
    setIsModalOpen(true);
  };

  return (
    <>
      {/* TABLE HEADING */}
      <div className="table-heading">{title}</div>

      <div className="table-box">
        <div className="table-responsive">
          <table className="plans-table table-bordered">
            <thead>
              <tr>
                <th>Plan</th>
                {type !== "storage-services" && (
                  <>
                    <th>vCPUs</th>
                    <th>Memory (GB)</th>
                  </>
                )}
                <th>Storage (GB)</th>
                <th>Bandwidth</th>
                <th>Price/Month (₹)</th>
                <th>Get Now</th>
              </tr>
            </thead>

            <tbody>
              {plans.length === 0 && (
                <tr className="mobile-card">
                  <td colSpan="7" className="text-center">
                    No plans found
                  </td>
                </tr>
              )}

              {plans.map((p) => (
                <tr key={p.id} className="mobile-card">
                  <td data-label="Plan">{p.title}</td>

                  {type !== "storage-services" && (
                    <>
                      <td data-label="vCPUs">{p.cpu}</td>
                      <td data-label="Memory (GB)">{p.ram}</td>
                    </>
                  )}

                  <td data-label="Storage (GB)">{p.storage}</td>

                  {/* Bandwidth */}
                  <td data-label="Bandwidth">{p.bandwidth}</td>

                  {/* Price */}
                  <td data-label="Price (₹)">₹{p.price}</td>

                  {/* Buy Button */}
                  <td data-label="Get Now">
                    <button className="buy-btn" onClick={() => openModal(p)}>
                      {p.button_title || "Buy"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* ===================== MODAL ===================== */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)}>
        {!submitted ? (
          <>
            <h2>Get In Touch</h2>

            <form onSubmit={formik.handleSubmit}>
              {/* NAME */}
              <div className="form-group">
                <input
                  type="text"
                  name="name"
                  placeholder="Name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.name}
                />
                {formik.touched.name && formik.errors.name && (
                  <small style={{ color: "red" }}>{formik.errors.name}</small>
                )}
              </div>

              {/* COMPANY */}
              <div className="form-group">
                <input
                  type="text"
                  name="company"
                  placeholder="Company Name"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.company}
                />
                {formik.touched.company && formik.errors.company && (
                  <small style={{ color: "red" }}>
                    {formik.errors.company}
                  </small>
                )}
              </div>

              {/* JOB */}
              <div className="form-group">
                <input
                  type="text"
                  name="job"
                  placeholder="Job Title"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.job}
                />
                {formik.touched.job && formik.errors.job && (
                  <small style={{ color: "red" }}>{formik.errors.job}</small>
                )}
              </div>

              {/* EMAIL */}
              <div className="form-group">
                <input
                  type="email"
                  name="email"
                  placeholder="Email Address"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.email}
                />
                {formik.touched.email && formik.errors.email && (
                  <small style={{ color: "red" }}>{formik.errors.email}</small>
                )}
              </div>

              {/* PHONE */}
              <div className="form-group">
                <input
                  type="text"
                  name="phone"
                  placeholder="Phone Number"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.phone}
                />
                {formik.touched.phone && formik.errors.phone && (
                  <small style={{ color: "red" }}>{formik.errors.phone}</small>
                )}
              </div>

              {/* CITY */}
              <div className="form-group">
                <input
                  type="text"
                  name="city"
                  placeholder="City"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.city}
                />
                {formik.touched.city && formik.errors.city && (
                  <small style={{ color: "red" }}>{formik.errors.city}</small>
                )}
              </div>

              {/* DESCRIPTION */}
              <div className="form-group">
                <textarea
                  rows="4"
                  name="description"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.description}
                />
                {formik.touched.description && formik.errors.description && (
                  <small style={{ color: "red" }}>
                    {formik.errors.description}
                  </small>
                )}
              </div>
              <ReCAPTCHA
                sitekey={SITE_KEY_V2}
                onChange={(token) => setCaptchaToken(token)}
                onExpired={() => setCaptchaToken(null)}
              />

              <button type="submit" className="submit-btn">
                Submit
              </button>
            </form>
          </>
        ) : (
          // SUCCESS SCREEN
          <div className="thanks-msg">
            <div className="main-container">
              <div className="check-container">
                <div className="check-background">
                  <svg viewBox="0 0 65 51">
                    <path
                      d="M7 25L27.3 44L58.5 7"
                      stroke="white"
                      strokeWidth="13"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                </div>
              </div>

              <h4>"Thank you — we'll contact you shortly."</h4>

              <button
                className="submit-btn"
                onClick={() => setIsModalOpen(false)}
                style={{ marginTop: "15px" }}
                disabled={!captchaToken}
              >
                Close
              </button>

            </div>
          </div>
        )}
      </Modal>
    </>
  );
}
